<template>
  <div
    v-if="!readOnly"
    :class="`input-product ${wrapperClass} ${isEffectivelyDisabled ? 'disabled-input' : ''} ${loading ? 'loading' : ''}`"
  >
    <v-btn
      v-if="!hidePlusMinusButtons"
      class="quantity-minus"
      icon
      @click="minusQty"
    >
      <v-icon size="14">mdi-minus</v-icon>
    </v-btn>
    <input
      v-bind="$attrs"
      ref="input"
      :value="internalValue"
      class="input-number"
      :class="[inputClasses, { 'at-max-stock': isAtMaxStock }]"
      :label="inputLabel ?? $t('general.Cantidad')"
      type="number"
      :step="dynamicStep"
      :data-min="dynamicMin"
      :max="dynamicMax"
      :min="internalValue > dynamicMin ? 0 : dynamicMin"
      :disabled="isEffectivelyDisabled"
      @change="debounceUpdateInternalValue($event.target.value)"
      @input="debounceUpdateInternalValue($event.target.value)"
    />
    <v-btn
      v-if="!hidePlusMinusButtons"
      class="quantity-plus"
      icon
      @click="plusQty"
    >
      <v-icon size="14">mdi-plus</v-icon>
    </v-btn>
  </div>
  <div v-else>
    {{ internalValue }}
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  props: {
    value: {
      type: Number,
      default: null
    },
    product: {
      type: Object,
      required: true,
      validator: function (value) {
        return value && value.CODE;
      }
    },
    inputClasses: {
      type: [String, Array, Object],
      default: ""
    },
    wrapperClass: {
      type: [String, Array, Object],
      default: ""
    },
    inputLabel: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    hidePlusMinusButtons: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      internalValue: 0,
      hasError: false,
      debounceChange: false
    };
  },
  computed: {
    ...mapGetters("config", [
      "comercialLoginAvalible",
      "comercialInfo",
      "ignoreStockByModel"
    ]),
    ...mapGetters("modelo", ["modelCode", "modelIdsap"]),
    isIntranetMode() {
      return this.comercialLoginAvalible && this.comercialInfo;
    },
    stockRuleModelId() {
      return this.modelIdsap || this.modelCode;
    },
    // Calculamos el step que debemos poner
    dynamicStep() {
      if (this.product && this.product.STEP) {
        return this.product.STEP;
      }

      return 1;
    },
    // Calculamos el min que debemos poner
    dynamicMin() {
      let minValue = 0;

      if (this.product && this.product.MOQ) {
        minValue = this.product.MOQ;
      }

      if (this.dynamicMax !== null && this.dynamicMax < minValue) {
        return this.dynamicMax;
      }

      return minValue;
    },
    dynamicMax() {
      if (this.ignoreStockByModel(this.stockRuleModelId)) {
        return null;
      }

      if (this.isIntranetMode) {
        return null;
      }

      if (!this.product || !this.product.STOCK) {
        return null;
      }

      let stockValue = Number(this.product.STOCK.stock);

      if (Number.isNaN(stockValue)) {
        return null;
      }

      return Math.max(stockValue, 0);
    },
    isAtMaxStock() {
      if (this.dynamicMax === null || this.dynamicMax <= 0) {
        return false;
      }

      return Number(this.internalValue) === this.dynamicMax;
    },
    isDisabledByStock() {
      return this.dynamicMax !== null && this.dynamicMax === 0;
    },
    isEffectivelyDisabled() {
      return this.disabled || this.isDisabledByStock;
    }
  },
  watch: {
    value(newValue) {
      let nextValue = newValue;

      if (this.dynamicMax !== null && Number(nextValue) > this.dynamicMax) {
        nextValue = this.dynamicMax;
      }

      if (nextValue !== this.internalValue) this.internalValue = nextValue;
    },
    dynamicMax(newVal, oldVal) {
      if (newVal === 0 && oldVal === null && this.internalValue > 0) {
        this.internalValue = 0;
        this.$emit("input", 0);
      }
    }
  },
  mounted() {
    let initialValue = this.value ?? 0;

    if (this.dynamicMax !== null && Number(initialValue) > this.dynamicMax) {
      this.internalValue = this.dynamicMax;
      return;
    }

    this.internalValue = initialValue;
  },
  methods: {
    handleKeyUp(event) {
      this.$emit("keyup-event", event);
    },
    plusQty: function () {
      let actualQty = Number(this.internalValue);

      if (!this.isEffectivelyDisabled) {
        let nextValue = actualQty + this.dynamicStep;

        if (actualQty == 0) {
          nextValue = this.dynamicMin;
        }

        if (this.dynamicMax !== null && nextValue > this.dynamicMax) {
          nextValue = this.dynamicMax;
        }

        this.updateInternalValue(nextValue);
      }
    },
    minusQty: function () {
      let actualQty = Number(this.internalValue);

      if (this.isEffectivelyDisabled) return;

      if (actualQty > this.dynamicMin) {
        this.updateInternalValue(actualQty - this.dynamicStep);
      } else {
        this.updateInternalValue(0);
      }
    },
    async debounceUpdateInternalValue(newVal) {
      if (this.debounceChange) {
        clearTimeout(this.debounceChange);
      }

      if (this.internalValue == newVal) {
        return;
      }

      this.debounceChange = setTimeout(() => {
        this.updateInternalValue(newVal);
      }, 450);
    },
    async updateInternalValue(newValue) {
      let normalizedValue = newValue;

      if (newValue !== "") {
        normalizedValue = Number(newValue);
      }

      if (normalizedValue == 0) {
        // console.log("CERO");
        this.internalValue = 0;
      } else if (normalizedValue === "") {
        // console.log("VACIO");
        this.internalValue = 1;
      } else if (normalizedValue <= this.dynamicMin) {
        // console.log("MINIMO");
        this.internalValue = this.dynamicMin;
      } else if (this.dynamicMax !== null && normalizedValue >= this.dynamicMax) {
        this.internalValue = this.dynamicMax;
      } else if (normalizedValue % this.dynamicStep !== 0) {
        // console.log("REDONDEO");
        this.internalValue =
          Math.ceil(normalizedValue / this.dynamicStep) * this.dynamicStep;

        if (
          this.dynamicMax !== null &&
          this.internalValue > this.dynamicMax
        ) {
          this.internalValue = this.dynamicMax;
        }

        if (normalizedValue != this.internalValue) {
          this.$refs.input.setCustomValidity("");
          this.$refs.input.reportValidity();
          await this.$nextTick();
        }

        let isInputInvalid = this.$refs.input.validity.valid == false;

        if (isInputInvalid && this.$refs.input.validity.stepMismatch) {
          if (normalizedValue == this.dynamicMin) {
            this.$refs.input.setCustomValidity("");
          }
        }
      } else {
        this.internalValue = normalizedValue;
      }

      this.$emit("input", this.internalValue);
    }
  }
};
</script>

<style lang="scss">
@keyframes loading {
  0% {
    border-bottom: 1.5px solid black;
  }
  30% {
    border-bottom: 1.5px solid var(--v-primary-base);
  }
  70% {
    border-bottom: 1.5px solid var(--v-primary-base);
  }
  100% {
    border-bottom: 1.5px solid black;
  }
}

.input-product {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  // border-bottom: 1.5px solid black;
  margin-bottom: 1.5px;
  // margin: 10px 0;
  transition: all 0.4s ease;

  input[type="number"]::-webkit-outer-spin-button,
  input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  } /* Para Firefox */
  input[type="number"] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  &.disabled-input {
    pointer-events: none;
    opacity: 0.5;
  }

  &.loading {
    animation: loading 2s linear infinite;
  }

  &:hover,
  &:active,
  &:focus-within {
    border-bottom: 1.5px solid var(--v-primary-base);
    margin-bottom: 0px;
  }

  input.input-number {
    all: unset;
    height: 100%;
    font-size: 14px !important;
    text-align: center;
    max-width: 50px !important;

    &.at-max-stock {
      color: var(--v-error-base, #ff5252);
    }
  }
}
</style>
